// Libs
import React from 'react'
import { FE_LABS_URL } from 'hfty'

// Containers
import PrivacyPolicyContainer from '../containers/PrivacyPolicy'

const styles = {
  eulaStyle: {
    padding: '7%',
    backgroundColor: 'white',
    lineHeight: '17px',
    fontSize: 13,
  },
  scrollableEulaStyle: {
    padding: '7%',
    backgroundColor: 'white',
    lineHeight: '1.2em',
    overflow: 'auto',
    height: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  bold: {
    fontWeight: 'bold',
  },
  underlined: {
    textDecoration: 'underline',
  },
  centered: {
    display: 'block',
    textAlign: 'center',
  },
}

type Props = {
  notScrollable: ?boolean,
}
const incLetter = (letter) => String.fromCharCode(letter)

const PrivacyPolicy = (props: Props) => {
  const eulaStyle = props.notScrollable ? styles.eulaStyle : styles.scrollableEulaStyle
  return (
    <div data-test="PRIVACY-POLICY" style={eulaStyle}>
      {title()}
      {intro()}
      {newLine()}
      {section1Definitions()}
      {section2Collection()}
      {section3PurposeAndUse()}
      {section4Disclosure()}
      {section5Safeguarding()}
      {section6Accuracy()}
      {section7Complaints()}
      {section8WithdrawingConsent()}
      {section9NonPersonalInformation()}
      {section10OtherRights()}
      {section11Inquiries()}
      {sectionFarmersEdgeInfo()}
      {section12Unsubscribe()}
      {section13PrivacyPolicyChanges()}
      {version()}
    </div>
  )
}

// utils
const newLine = () => <br />
const tab = (numberOfTabs) => {
  const tabWidth = 25
  return {
    marginLeft: `${numberOfTabs * tabWidth}px`,
  }
}

// utilityStyles
const letterListStyle = { display: 'block', ...tab(2) }
const letterStyle = {}
const bold = styles.bold
const boldQuotes = (text: string) => {
  return (
    <span>
      "<span style={bold}>{text}</span>"
    </span>
  )
}
// components
const title = () => (
  <div
    style={{
      ...bold,
      ...styles.underlined,
      ...styles.centered,
    }}
  >
    <span>PRIVACY POLICY</span>
    {newLine()}
  </div>
)

const intro = () => (
  <div data-test="PRIVACY-POLICY-intro">
    <div>
      <p>
        The website <a href={FE_LABS_URL} target="_blank" rel="noopener noreferrer">{FE_LABS_URL}</a> (the {boldQuotes('Site')}) is operated by
        Farmers Edge Inc. (also referred to as “<span style={bold}>Farmers Edge</span>”, “<span style={bold}>us</span>”,
        or “<span style={bold}>we</span>”). We respect your right to control your personal information. That is why we
        have developed this Privacy Policy which governs and explains how we collect, use and disclose your personal
        information (the “<span style={bold}>Privacy Policy</span>”).
      </p>
      <p>
        This page contains the Privacy Policy for the Site, as well as for any other websites or microsites operated by
        Farmers Edge (collectively and together with the Site, the “<span style={bold}>Sites</span>”), and for web
        applications, tools, forums, or social media pages offered by Farmers Edge on the Sites, and for any online
        services offered by Farmers Edge, including but not limited to the FE soils nutrient management portal offered
        by Farmers Edge (together with the Sites, the “<span style={bold}>Service</span>”).
      </p>
    </div>
    <div style={bold}>
      <p>
        By accessing or using the Service, you consent to the collection, use and disclosure of personal information by
        Farmers Edge in accordance with this Privacy Policy. You may choose not to provide us with personal information.
        However, if you make this choice we may not be able to provide you with the application, service or information
        that you requested.
      </p>
    </div>
  </div>
)
// Going to break this up into sections, and there are 11 of them

const section1Definitions = () => {
  return (
    <div data-test="Section-1-Definitions">
      <span style={bold}>1.</span>
      <span style={{ ...bold, ...tab(1) }}>Definitions</span>
      {newLine()}
      <span>For the purposes of this Privacy Policy, the following words have the following meanings:</span>
      {newLine()}
      <div data-test="section-1-definitions">
        {boldQuotes('collection')}
        <span>
          {' '}
          means the act of gathering, acquiring, recording or obtaining personal information from any source, including
          third parties, by any means.
        </span>
        {newLine()}
        {newLine()}
        {boldQuotes('consent')}
        <span>
          {' '}
          means voluntary agreement with the collection, use and disclosure of personal information for defined
          purposes. Consent can be either express or implied and can be provided directly by the individual or by an
          authorized representative, such as a parent or guardian. Express consent can be given orally, electronically
          or in writing. Implied consent is consent that can reasonably be inferred from a user's action or inaction.
        </span>
        {newLine()}
        {newLine()}
        {boldQuotes('user')}
        <span>
          {' '}
          means an individual who uses the Service provided by Farmers Edge, including anyone else who accesses or
          visits the Sites.
        </span>
        {newLine()}
        {newLine()}
        {boldQuotes('disclosure')}
        <span> means making personal information available to a third party.</span>
        {newLine()}
        {newLine()}
        {boldQuotes('employee')}
        <span>
          {' '}
          means an employee of Farmers Edge, and for the purpose of this Privacy Policy only, includes agents,
          independent and other contractors performing services for Farmers Edge.
        </span>
        {newLine()}
        {newLine()}
        {boldQuotes('personal information')}
        <span>
          {' '}
          means information about an identifiable individual, but not aggregated information that cannot be associated
          with a specific individual.
        </span>
        {newLine()}
        {newLine()}
        {boldQuotes('registered user')}
        <span>
          {' '}
          means a user who has created and registered an account with Farmers Edge, as well as a Subscribing
          Organization that has created and registered an account or accounts with Farmers Edge.
        </span>
        {newLine()}
        {newLine()}
        {boldQuotes('Subscribing Organization')}
        <span>
          {' '}
          means a company, entity or organization, including any agricultural business, that has subscribed to the
          Service.
        </span>
        {newLine()}
        {newLine()}
        {boldQuotes('third party')}
        <span>
          {' '}
          means an individual other than the user, or the user's agent, or an organization outside Farmers Edge or a
          Subscribing Organization.
        </span>
        {newLine()}
        {newLine()}
        {boldQuotes('use')}
        <span> means the treatment, handling and management of personal information by and within Farmers Edge.</span>
        {newLine()}
        {newLine()}
      </div>
    </div>
  )
}

const section2Collection = () => {
  let listCounter = 'a'.charCodeAt(0)
  return (
    <div data-test="Section-2-Collection">
      <span style={bold}>2.</span>
      <span style={{ ...bold, ...tab(1) }}>Collection</span>
      <p>
        Farmers Edge may collect any or all of the following personal information if the information is reasonably
        required to provide the services accessed or requested by you:
      </p>
      <div data-test="section-2-letter-list">
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            information that you provide when you become a registered user or otherwise complete forms on the Service,
            such as your name, email, postal address, telephone number, age, country of residence and occupation;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            your log-in and password details in connection with your Farmers Edge account(s), including your username;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            information that you provide to Farmers Edge when you subscribe to the Service, such as your billing
            address, payment method and credit card information;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            your correspondence with Farmers Edge employees, including any questions, concerns or comments you may have
            about the Service or problems that you report;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>your location information via your mobile device or certain of your farm equipment;</p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            comments, videos, photographs, and any other content or information that you may post or submit to the
            Service, including, but not limited to, information about your farming operation, land, crops, and/or
            equipment;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            information about your farming operation that may be automatically collected via the Service after you have
            subscribed to certain aspects of the Service, including, but not limited to (i) operational data such as
            seeding, field boundary delineations, shipping logistics, fertilizer application, ratios, soil samples,
            nutrient prescriptions, controller files, grid setups, spraying, harvesting, tillage, input supplies,
            service records, production histories, nutrient management, task management; (ii) equipment data such as
            fuel burned, fuel levels, engine RPM, GPS coordinates, heading, slippage, diagnostic trouble codes, engine
            hours, ground speed, pressure readings, depreciation, Power-Take Off (PTO), and working state; and (iii)
            soil data; and
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>details of your requests made through the Service.</p>
        </div>
      </div>
    </div>
  )
}

const section3PurposeAndUse = () => {
  let listCounter = 'a'.charCodeAt(0)
  return (
    <div data-test="Section-3-Purpose-And-Use">
      <span style={bold}>3.</span>
      <span style={{ ...bold, ...tab(1) }}>Purpose of Collection and Use of Personal Information</span>
      {newLine()}
      <span>
        Farmers Edge collects your personal information and will use your personal information as is reasonably required
        for the following purposes:
      </span>
      {newLine()}
      <div data-test="section-3-letter-list">
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>if you are a registered user, to establish and maintain your Farmers Edge account;</p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            to provide web applications, the Service, and/or the other services or information that you request,
            including, but not limited to, agricultural and agronomic analyses, reports, consulting services, and data
            management and analytics;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            to verify your identity if you submit comments, videos, photographs, opinions, or any other content or
            information to the Service;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            to respond to questions, comments, requests, job applications, or complaints that you submit to Farmers
            Edge;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>to process payments made by you to Farmers Edge;</p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>to collect amounts owing from you to Farmers Edge;</p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>to track communications with you;</p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>to analyze use of the Service and to improve the Service;</p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            to send emails, newsletters, regular mail or other communications you have consented to receive from Farmers
            Edge;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>to provide Service-related announcements or notifications to you;</p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>to fulfill a purpose that we disclose to you when we request your personal information; and/or</p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>to meet legal and regulatory requirements.</p>
        </div>
      </div>
      <p>
        Farmers Edge staff are authorized to access personal information based only on their need to deal with the
        information for the reason(s) for which it was obtained. Personal information will only be collected and used by
        authorized staff in a manner consistent with the activities of Farmers Edge.
      </p>
      {newLine()}
    </div>
  )
}

const section4Disclosure = () => {
  let listCounter = 'a'.charCodeAt(0)
  return (
    <div data-test="Section-4-Disclosure">
      <span style={bold}>4.</span>
      <span style={{ ...bold, ...tab(1) }}>Disclosure</span>
      {newLine()}
      <span>Farmers Edge may disclose your personal information to: </span>
      {newLine()}
      <div data-test="Section-4-Delivery-Letter-List">
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            its employees, agents, partners and service providers as is reasonably necessary to enable these parties to
            perform a business, professional or technical support function for Farmers Edge;
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>a person who, in the reasonable judgment of Farmers Edge, is seeking the information as your agent; and</p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>a third party or parties, where you consent to such disclosure or the disclosure is required by law.</p>
        </div>
      </div>
      <p>
        We may also disclose your personal information in connection with a corporate re-organization, a merger or
        amalgamation with another entity, a sale of all or a substantial portion of our assets or stock, including any
        due diligence exercise carried out in relation to the same, provided that the information disclosed continues to
        be used for the purposes permitted by this Privacy Policy by the entity acquiring the information.
      </p>
      <p>
        If you voluntarily submit or post any information, videos, photographs or other content (
        {boldQuotes('User Content')}) to the Service, certain of your personal information may be included in the
        posting, such as your user name and email address, and this information may be collected and used by others.
        Farmers Edge has no control over and is not liable for the collection, use or disclosure of any posted User
        Content or your included personal information by users of the Service. You are responsible for ensuring the
        protection of any of your personal information that you post to any other user’s Farmers Edge messaging account.
        The term “User Content” does not include aggregated non-personal data as described in Section 9 of this Privacy
        Policy.
      </p>
    </div>
  )
}

const section5Safeguarding = () => {
  return (
    <div data-test="Section-5-Blank">
      <span style={bold}>5.</span>
      <span style={{ ...bold, ...tab(1) }}>Safeguarding Your Personal Information</span>
      {newLine()}
      <p>
        Farmers Edge is committed to safeguarding your personal information. Farmers Edge uses appropriate security
        measures to protect personal information against risks such as loss or theft, unauthorized access, disclosure,
        copying, use, modification or destruction regardless of the format in which it is held. The methods of
        protection and safeguards used by Farmers Edge include, but are not limited to, locked filing cabinets,
        need-to-know access and technological measures, including the use of passwords, encryption and firewalls. While
        Farmers Edge cannot guarantee that risks such as loss, theft, unauthorized access or disclosure of personal
        information will not occur, Farmers Edge will make every effort to prevent such unfortunate occurrences.
      </p>
      <p>
        Farmers Edge takes care in disposing of or destroying personal information to prevent unauthorized parties from
        gaining access to the information. Farmers Edge also ensures that its employees with access to personal
        information are made aware of the importance of maintaining the confidentiality of personal information.
        Finally, Farmers Edge protects personal information disclosed to third parties by contractual agreements
        stipulating the confidentiality of the information and the purposes for which it is to be used.
      </p>
      <p>
        If you are a registered user and your Farmers Edge account becomes inactive, Farmers Edge may choose not to
        retain your User Content, personal information associated with your Farmers Edge account, or any other records
        associated with your Farmers Edge account, for a period longer than three years after your Farmers Edge account
        becomes inactive, in which case such information may be destroyed. If your Farmers Edge account becomes
        inactive, you are solely responsible for exporting any such information from your Farmers Edge account that you
        may wish to retain.
      </p>
    </div>
  )
}
const section6Accuracy = () => {
  return (
    <div data-test="Section-6-Accuracy">
      <span style={bold}>6.</span>
      <span style={{ ...bold, ...tab(1) }}>Accuracy</span>
      {newLine()}
      <p>
        Farmers Edge is committed to keeping your personal information as complete, up-to-date and accurate as is
        necessary for the purposes for which it is to be used. Farmers Edge will promptly correct or complete any
        personal information found to be inaccurate or incomplete. Where appropriate, Farmers Edge will transmit amended
        information to third parties having access to the personal information in question. Upon request, Farmers Edge
        will provide you with a reasonable opportunity to review the personal information in your file to review its
        accuracy. Such personal information will be provided within a reasonable time of the request and at a minimal or
        no cost to you.
      </p>
      <p>
        Also upon request, Farmers Edge will provide an account of the use and disclosure of your personal information
        and, where reasonably possible, will state the source of the information. In providing an account of disclosure,
        Farmers Edge will provide a list of organizations to which it may have disclosed personal information about you
        when it is not possible to provide an actual list.
      </p>
    </div>
  )
}
const section7Complaints = () => (
  <div data-test="Section-7-Complaints">
    <span style={bold}>7.</span>
    <span style={{ ...bold, ...tab(1) }}>Complaints</span>
    {newLine()}
    <p>
      Farmers Edge will promptly investigate all complaints concerning our compliance with the Privacy Policy and our
      dealings with your personal information. If a complaint is found to be justified, Farmers Edge will take
      appropriate measures to resolve the complaint, including, if necessary, amending our policies and procedures. You
      will be informed of the outcome of the investigation regarding your complaint.
    </p>
  </div>
)
const section8WithdrawingConsent = () => (
  <div data-test="Section-8-Withdrawing-Consent">
    <span style={bold}>8.</span>
    <span style={{ ...bold, ...tab(1) }}>Withdrawing Consent</span>
    {newLine()}
    <p>
      You may withdraw your consent to the collection, use or disclosure of your personal information by Farmers Edge at
      any time, subject to legal or contractual restrictions and reasonable notice. In certain circumstances, your
      withdrawal of consent will result in the inability of Farmers Edge to continue providing aspects of the Service to
      you. Please contact Farmers Edge for more information regarding the implications of withdrawing consent.
    </p>
  </div>
)
const section9NonPersonalInformation = () => {
  let listCounter = 'a'.charCodeAt(0)
  return (
    <div data-test="Section-9-Non-Personal-Information">
      <span style={bold}>9.</span>
      <span style={{ ...bold, ...tab(1) }}>Non-Personal Information</span>
      {newLine()}
      <p>
        Non-personal information is information that does not identify you and which we are unable to connect to other
        information in order to identify you. Farmers Edge may collect any or all of the following non-personal
        information:
      </p>
      <div data-test="Section-9-Letter-List">
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            Aggregated data resulting from the combination and/or aggregation of certain raw data collected from lands
            and farm equipment (including, but not limited to, farm equipment information, operational data, field
            applications data, harvest data, field-centric weather data, soil data, and carbon data) with other data in
            a way that no longer personally identifies the user.
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            The Sites employ a web analytics service that uses "cookies," which are text files placed on your computer,
            to help the website analyze how users use the Sites and to override any security restrictions based on the
            IP address the visitor is coming from. The information generated by the cookies about your use of the Sites
            (including your IP address) will be transmitted to and stored by web analytics service providers on servers
            in the United States. The web analytics service providers will use this information for the purpose of
            evaluating your use of the Sites, compiling reports on Sites activity for the Sites’ operators and providing
            other services relating to website activity and Internet usage. The web analytics service providers may also
            transfer this information to third parties where required to do so by law, or where such third parties
            process the information on the web analytics service providers’ behalf. The web analytics service providers
            will not associate your IP address with any other data held by the web analytics service provider. The web
            analytics service is strictly necessary for the security operations of the Sites and cannot be turned off.
            By using the Sites, you consent to the processing of data about you by web analytics service providers in
            the manner and for the purposes set out above.
          </p>
        </div>
        <span style={letterStyle}>{`(${incLetter(listCounter++)})`}</span>
        <div style={letterListStyle}>
          <p>
            We may also use additional analytics programs from time to time to process non-personal information about
            you.
          </p>
        </div>
      </div>
      <p>
        We make no attempt to link this non-personal information with the identity of individuals using the Service.
        Non-personal information may be permanently archived for future use in any manner whatsoever by Farmers Edge.
      </p>
    </div>
  )
}
const section10OtherRights = () => (
  <div data-test="Section-10-OtherRights">
    <span style={bold}>10.</span>
    <span style={{ ...bold, ...tab(1) }}>Other Rights</span>
    {newLine()}
    <p>
      You may have other rights in relation to personal information held by Farmers Edge, such as a right to
      erasure/deletion, to restrict or object to us processing personal data and the right to data portability. For
      further information in relation to legal rights under applicable law or exercising any of them, please contact us
      by using the information in the “Inquiries” section below at any time.
    </p>
  </div>
)
const section11Inquiries = () => (
  <div data-test="Section-11-Inquiries">
    <span style={bold}>11.</span>
    <span style={{ ...bold, ...tab(1) }}>Inquiries</span>
    {newLine()}
    <p>
      To submit an inquiry, complaint, request a review of your personal information, correct or update your personal
      information, withdraw your consent or request more information or additional copies of the Privacy Policy contact:
    </p>
  </div>
)
const sectionFarmersEdgeInfo = () => (
  <div data-test="farmers-edge-info">
    <span style={bold}>Farmers Edge Inc.</span>
    {newLine()}
    <p>Mailing Address: 25 Rothwell Road, Winnipeg, MB R3P 2M5</p>
    <p>Telephone No.: 1 (800) 845-8171</p>
    <p>
      E-mail: <span style={{ ...bold, ...styles.underlined }}>fesoils.helpdesk@farmersedge.ca</span>
    </p>
    {newLine()}
  </div>
)
const section12Unsubscribe = () => (
  <div data-test="Unsubscribe">
    <span style={bold}>12.</span>
    <span style={{ ...bold, ...tab(1) }}>Unsubscribe</span>
    {newLine()}
    <p>
      If you no longer wish to receive commercial electronic messages from Farmers Edge regarding our new products or
      services, promotions or other similar communications, you may unsubscribe here
    </p>
    <p>support@farmersedge.ca</p>
    <p>
      Please note that, after you unsubscribe, Farmers Edge may still send electronic communications to you in certain
      limited circumstances, such as to provide you with Service notifications and billing information, or to provide
      information that Farmers Edge is legally obligated to provide you.
    </p>
    {newLine()}
  </div>
)
const section13PrivacyPolicyChanges = () => (
  <div data-test="Section13-Privacy-Policy-Changes">
    <span style={bold}>13.</span>
    <span style={{ ...bold, ...tab(1) }}>Privacy Policy Changes</span>
    {newLine()}
    <p>
      To accommodate changes in our services, changes in technology and legal developments, this Privacy Policy may
      change over time without notice to you. We may add, change or remove portions of the Privacy Policy when we feel
      it is appropriate to do so. We encourage you to review our Privacy Policy periodically. Each time you submit
      personal information or use the Service you agree to be bound by terms of the Privacy Policy which is then
      current. Whenever we update the Privacy Policy, we will change the date below to indicate when the changes were
      made.
    </p>
    {newLine()}
  </div>
)

const version = () => {
  return (
    <div data-test="Version">
      {newLine()}
      <span style={bold}>Date: June 1, 2018</span>
      {newLine()}
      <span style={bold}>Document Number: 2694353 Version: 1A</span>
    </div>
  )
}

export default PrivacyPolicyContainer(PrivacyPolicy)
